import { Box, Button, Grid2, Typography } from "@mui/material";
import axios from "axios";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

const Step1 = ({ sra, setSra, setStep, storedUser }) => {
  const navigate = useNavigate();
  const [errors, setErrors] = useState({});
  const [btnLoading, setBtnLoading] = useState(false);
  const orgId = JSON.parse(localStorage.getItem("org"));
  const [vendors, setVendors] = useState([]);

  useEffect(() => {
    const getVendors = async () => {
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/vendor-list/org?orgId=${orgId}`,
          {
            headers: {
              Authorization: `Bearer ${storedUser.accessToken}`,
            },
          },
        );

        setVendors(res.data);
      } catch (error) {
        console.log(error.response?.data?.Exception || "Access denied");
      }
    };

    getVendors();
  }, []);

  const handleVendorChange = (e) => {
    const selectedCode = e.target.value;

    const selectedVendor = vendors.find(
      (vendor) => vendor.code === selectedCode,
    );

    if (selectedVendor) {
      setSra((prev) => ({
        ...prev,
        vendorCode: selectedVendor.code || "",
      }));
    } else {
      setSra((prev) => ({
        ...prev,
        vendorCode: "",
      }));
    }

    setErrors((prev) => ({
      ...prev,
      vendorCode: "",
      name: "",
    }));
  };

  const handleChange = (e) => {
    setSra({ ...sra, [e.target.name]: e.target.value });
    setErrors({ ...errors, [e.target.name]: "" });
  };

  const validateForm = () => {
    const newErrors = {};
    const data = sra || {};

    const isEmpty = (value) => !value || value.trim?.() === "";

    if (isEmpty(data.title)) {
      newErrors.title = "Title is required";
    }

    // Tender ID
    if (isEmpty(data.tenderId)) {
      newErrors.tenderId = "Tender ID is required";
    }

    // Tender Name
    if (isEmpty(data.tenderName)) {
      newErrors.tenderName = "Tender Name is required";
    }

    // Department
    if (isEmpty(data.dept)) {
      newErrors.dept = "Department is required";
    }

    // Date
    if (!data.date) {
      newErrors.date = "Date of Tender Published is required";
    } else {
      const selectedDate = new Date(data.date);
      const today = new Date();

      today.setHours(0, 0, 0, 0);

      if (selectedDate > today) {
        newErrors.date = "Date of Tender Published cannot be a future date";
      }
    }

    // Company Name
    if (isEmpty(data.companyName)) {
      newErrors.companyName = "Company Name is required";
    }

    // Address
    if (isEmpty(data.address)) {
      newErrors.address = "Address is required";
    } else if (data.address.length > 250) {
      newErrors.address = "Address must be up to 250 characters";
    }

    // Contact
    if (isEmpty(data.contact)) {
      newErrors.contact = "Contact Bidder is required";
    } else if (!/^\d{10}$/.test(data.contact)) {
      newErrors.contact = "Contact Bidder must be a valid 10-digit number";
    }

    // Additional Info
    if (isEmpty(data.info)) {
      newErrors.info = "Company Information is required";
    } else if (data.info.length > 1024) {
      newErrors.info = "Company Information must be up to 1024 characters";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = async () => {
    if (!validateForm()) return;
    setBtnLoading(true);
    try {
      const res = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/draft-tender-deposit`,
        {
          username: storedUser.username,
          title: sra.title,
          sapNumber: sra.sapNumber,
          vendorCode: sra.vendorCode,
          tenderId: sra.tenderId,
          tenderName: sra.tenderName,
          dept: sra.dept,
          date: sra.date,
          companyName: sra.companyName,
          address: sra.address,
          contact: sra.contact,
          info: sra.info,
        },
        { headers: { Authorization: `Bearer ${storedUser.accessToken}` } },
      );
      setSra((prev) => ({
        ...prev,
        ...res.data,
        amount: prev.amount || "",
        transactionId: prev.transactionId || "",
        method: prev.method || "",
        attachment1: prev.attachment1 || "",
      }));
      setStep((prev) => prev + 1);
    } catch (error) {
      console.log(error);
    } finally {
      setBtnLoading(false);
    }
  };

  const handleUpdate = async () => {
    if (!validateForm()) return;
    setBtnLoading(true);
    try {
      await axios.put(
        `${process.env.REACT_APP_BACKEND_URL}/draft-tender-deposit?draftSraId=${sra.draftTenderDepositId}`,
        {
          title: sra.title,
          sapNumber: sra.sapNumber,
          vendorCode: sra.vendorCode,
          tenderId: sra.tenderId,
          tenderName: sra.tenderName,
          dept: sra.dept,
          date: sra.date,
          companyName: sra.companyName,
          address: sra.address,
          contact: sra.contact,
          info: sra.info,
        },
        { headers: { Authorization: `Bearer ${storedUser.accessToken}` } },
      );
      setStep((prev) => prev + 1);
    } catch (error) {
      console.log(error);
    } finally {
      setBtnLoading(false);
    }
  };

  const handleBack = () => {
    navigate("/dashboard");
  };

  return (
    <div style={{ position: "relative" }}>
      {btnLoading && <div className="loading-div"></div>}
      <h2>Tender Deposit Form</h2>
      <div className="progress-container">
        <span className="blue-bar" style={{ width: "20%" }}></span>
      </div>
      <div className="dashboard-container">
        <Typography className="form-title">Details</Typography>

        <Grid2 container columnSpacing={2}>
          <Grid2 size={{ xs: 12, sm: 4 }}>
            <label htmlFor="title">
              Title <span style={{ color: "#FF0000" }}>*</span>
            </label>
            <input
              className={`${errors.title && "input-error"}`}
              type="text"
              name="title"
              id="title"
              onChange={handleChange}
              value={sra?.title || ""}
            />
            {errors.title && <p className="error">{errors.title}</p>}
          </Grid2>
          <Grid2 size={{ xs: 12, sm: 4 }}>
            <label htmlFor="sapNumber">SAP Document Number</label>
            <input
              className={`${errors.sapNumber && "input-error"}`}
              type="text"
              name="sapNumber"
              id="sapNumber"
              onChange={handleChange}
              value={sra?.sapNumber || ""}
            />
            {errors.sapNumber && <p className="error">{errors.sapNumber}</p>}
          </Grid2>
          <Grid2 size={{ xs: 12, sm: 4 }}>
            <label htmlFor="vendorCode">Vendor Code</label>
            <select
              name="vendorCode"
              id="vendorCode"
              onChange={handleVendorChange}
              value={sra?.vendorCode|| ""}
              className={`${errors.code && "input-error"}`}
            >
              <option value="">---Select---</option>
              {vendors?.map((vendor) => (
                <option key={vendor.code} value={vendor.code}>
                  {vendor.code}
                </option>
              ))}
            </select>
            {errors.vendorCode && <p className="error">{errors.vendorCode}</p>}
          </Grid2>
          <Grid2 size={{ xs: 12, sm: 4 }}>
            <label htmlFor="tenderId">
              Tender ID <span style={{ color: "#FF0000" }}>*</span>
            </label>
            <input
              className={`${errors.tenderId && "input-error"}`}
              type="text"
              name="tenderId"
              id="tenderId"
              onChange={handleChange}
              value={sra?.tenderId || ""}
            />
            {errors.tenderId && <p className="error">{errors.tenderId}</p>}
          </Grid2>

          <Grid2 size={{ xs: 12, sm: 4 }}>
            <label htmlFor="tenderName">
              Tender Name <span style={{ color: "#FF0000" }}>*</span>
            </label>
            <input
              className={`${errors.tenderName && "input-error"}`}
              type="text"
              name="tenderName"
              id="tenderName"
              onChange={handleChange}
              value={sra?.tenderName || ""}
            />
            {errors.tenderName && <p className="error">{errors.tenderName}</p>}
          </Grid2>

          <Grid2 size={{ xs: 12, sm: 4 }}>
            <label htmlFor="dept">
              Department <span style={{ color: "#FF0000" }}>*</span>
            </label>
            <input
              className={`${errors.dept && "input-error"}`}
              type="text"
              name="dept"
              id="dept"
              onChange={handleChange}
              value={sra?.dept || ""}
            />
            {errors.dept && <p className="error">{errors.dept}</p>}
          </Grid2>

          <Grid2 size={{ xs: 12, sm: 4 }}>
            <label htmlFor="date">
              Date of Tender Published{" "}
              <span style={{ color: "#FF0000" }}>*</span>
            </label>
            <input
              className={`${errors.date && "input-error"}`}
              type="date"
              name="date"
              id="date"
              onChange={handleChange}
              value={sra?.date || ""}
            />
            {errors.date && <p className="error">{errors.date}</p>}
          </Grid2>

          <Grid2 size={{ xs: 12, sm: 4 }}>
            <label htmlFor="companyName">
              Company Name <span style={{ color: "#FF0000" }}>*</span>
            </label>
            <input
              className={`${errors.companyName && "input-error"}`}
              type="text"
              name="companyName"
              id="companyName"
              onChange={handleChange}
              value={sra?.companyName || ""}
            />
            {errors.companyName && (
              <p className="error">{errors.companyName}</p>
            )}
          </Grid2>

          <Grid2 size={{ xs: 12, sm: 4 }}>
            <label htmlFor="address">
              Address <span style={{ color: "#FF0000" }}>*</span>
            </label>
            <input
              className={`${errors.address && "input-error"}`}
              type="text"
              name="address"
              id="address"
              onChange={handleChange}
              value={sra?.address || ""}
            />
            {errors.address && <p className="error">{errors.address}</p>}
          </Grid2>

          <Grid2 size={{ xs: 12, sm: 4 }}>
            <label htmlFor="contact">
              Contact Bidder <span style={{ color: "#FF0000" }}>*</span>
            </label>
            <input
              className={`${errors.contact && "input-error"}`}
              type="text"
              name="contact"
              id="contact"
              onChange={handleChange}
              value={sra?.contact || ""}
            />
            {errors.contact && <p className="error">{errors.contact}</p>}
          </Grid2>

          <Grid2 size={{ xs: 12, sm: 12 }}>
            <label htmlFor="info">
              Company Information <span style={{ color: "#FF0000" }}>*</span>
            </label>
            <textarea
              className={`${errors.info && "input-error"}`}
              name="info"
              id="info"
              onChange={handleChange}
              value={sra?.info || ""}
              rows={4}
            />
            {errors.info && <p className="error">{errors.info}</p>}
          </Grid2>
        </Grid2>
      </div>

      <Box className="btn-container" sx={{ gap: 3 }}>
        <Button className="btn-outlined" onClick={handleBack}>
          Back
        </Button>
        <Button
          className="btn-contained"
          onClick={sra && sra.draftTenderDepositId ? handleUpdate : handleNext}
          disabled={btnLoading}
        >
          {btnLoading ? "Submitting..." : "Next"}
        </Button>
      </Box>
    </div>
  );
};

export default Step1;
